import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { usePayments, useVoidPayment } from 'hooks/api/usePayments';
import type { Payment, PaymentSearchRequest } from 'api/types';
import { usePaymentModes } from 'hooks/api/usePaymentModes';
import type { SelectOption } from 'components/ui/SyutSelect';
import PaymentCaptureModal from 'components/modals/modules/payment-module/PaymentCaptureModal';
import PaymentVerificationBadge from 'components/modules/payment-module/PaymentVerificationBadge';
import ReceiptNumber from 'components/common/billing/ReceiptNumber';

type ViewMode = 'list' | 'grid';

const includeVoidedOptions: SelectOption[] = [
  { value: 'false', label: 'Active Only' },
  { value: 'true', label: 'Include Voided' },
];

interface AdvancePaymentsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const formatCurrency = (val: number) =>
  val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AdvancePaymentsScreen: React.FC<AdvancePaymentsScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<PaymentSearchRequest>({
    page: 1,
    pageSize: 25,
    paymentType: 'Advance',
    includeVoided: false,
  });
  const { data, pagination, refetch } = usePayments(queryParams);
  const { voidPayment, loading: isVoiding } = useVoidPayment();
  // SYSTEM-scope payment modes (no propertyId) — drives the cashier filter dropdown.
  const { modes: enabledModes } = usePaymentModes();
  const paymentModeOptions = useMemo<SelectOption[]>(
    () => [{ value: '', label: 'All Modes' }, ...enabledModes.map(m => ({ value: m, label: m }))],
    [enabledModes]
  );

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterMode, setFilterMode] = useState('');
  const [filterIncludeVoided, setFilterIncludeVoided] = useState('false');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<Payment | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleEdit = useCallback((item: Payment) => {
    if (!item.isActive) {
      notificationService.toast.warning('Voided payments cannot be edited.');
      return;
    }
    const modalId = tabId ? `${tabId}-form-modal-edit-payment-${item.paymentId}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(item);
    setEditModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreModal = useCallback((_mode: 'create' | 'edit', recordId?: string) => {
    if (recordId) {
      const item = data.find(o => o.paymentId === recordId);
      if (item) setSelectedRecord(item);
    }
    setEditModalOpen(true);
  }, [data]);

  const handleVoidClick = useCallback((item: Payment) => {
    if (!item.isActive) {
      notificationService.toast.warning('Payment is already voided.');
      return;
    }
    setSelectedRecord(item);
    setVoidConfirmOpen(true);
  }, []);

  const handleConfirmVoid = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await voidPayment(selectedRecord.paymentId, selectedRecord.rowVersion);
      setVoidConfirmOpen(false);
      notificationService.toast.success(`Payment of ₹${formatCurrency(selectedRecord.amount)} voided successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.errors?.[0] || 'Failed to void payment.';
      notificationService.toast.error(msg);
    }
  }, [selectedRecord, voidPayment, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setEditModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  // SyutGrid requires `id` on each row; map paymentId → id (the rest of Payment is preserved).
  const gridData = useMemo(() => data.map(p => ({ ...p, id: p.paymentId })), [data]);
  type PaymentRow = Payment & { id: string };

  const tableColumns: SyutGridColumn<PaymentRow>[] = useMemo(() => [
    {
      id: 'paymentDate', header: 'Date', accessorKey: 'paymentDate', size: 110, enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue() as string;
        return <span style={{ fontSize: '0.85rem' }}>{v ? new Date(v).toLocaleDateString('en-IN') : '-'}</span>;
      },
    },
    { id: 'bookingNumber', header: 'Booking #', accessorKey: 'bookingNumber', size: 140, enableSorting: true },
    { id: 'propertyName', header: 'Property', accessorKey: 'propertyName', size: 180 },
    { id: 'guestFullName', header: 'Guest', accessorKey: 'guestFullName', size: 180 },
    {
      id: 'amount', header: 'Amount (₹)', accessorKey: 'amount', size: 120, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', fontWeight: 500, fontFamily: 'monospace' }}>{formatCurrency(getValue() as number)}</span>,
    },
    { id: 'paymentMode', header: 'Mode', accessorKey: 'paymentMode', size: 120, enableSorting: true },
    {
      id: 'paymentReference', header: 'Reference', accessorKey: 'paymentReference', size: 160,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'receivingBankAccount', header: 'Receiving Account', accessorKey: 'receivingBankAccountCode', size: 200,
      cell: ({ row }) => {
        const code = row.original.receivingBankAccountCode;
        const bankName = row.original.receivingBankName;
        const isCheque = row.original.paymentMode === 'Cheque';
        if (!code) {
          return isCheque
            ? <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>Pending deposit</span>
            : <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>-</span>;
        }
        return <span style={{ fontSize: '0.85rem' }}>{code} ({bankName})</span>;
      },
    },
    {
      id: 'paymentStatusCode', header: 'Status', accessorKey: 'paymentStatusCode', size: 120,
      cell: ({ row }) => {
        const code = row.original.paymentStatusCode;
        const label = row.original.paymentStatus || code;
        const color = row.original.paymentStatusColor || 'success';
        return <span className={`badge badge-${color}`} style={{ fontSize: '0.7rem' }}>{label}</span>;
      },
    },
    {
      id: 'verificationStatus', header: 'Bank Verification', accessorKey: 'verificationStatus', size: 160,
      cell: ({ row }) => <PaymentVerificationBadge payment={row.original} />,
    },
    {
      id: 'receiptNumber', header: 'Receipt #', accessorKey: 'receiptNumber', size: 160,
      cell: ({ row }) => <ReceiptNumber receiptNumber={row.original.receiptNumber} state={row.original.receiptNumberState} />,
    },
    {
      id: 'acctTxnRef', header: 'Fairbooks Ref', accessorKey: 'acctTxnRef', size: 150,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'isActive', header: 'Active', accessorKey: 'isActive', size: 90,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>{isActive ? 'Active' : 'Voided'}</span>;
      },
    },
  ], []);

  const getRowActions = useCallback((item: PaymentRow): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(item), disabled: !item.isActive });
    }
    // 'void' is destructive — gate on 'delete'
    if (allowedActions.includes('delete')) {
      items.push({ id: 'void', label: 'Void', icon: <IconSystem.trash size={18} />, onClick: () => handleVoidClick(item), variant: 'danger', disabled: !item.isActive });
    }
    return items;
  }, [allowedActions, handleEdit, handleVoidClick]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' },
  ], [refetch]);

  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      paymentMode: filterMode || undefined,
      paymentDateFrom: filterDateFrom || undefined,
      paymentDateTo: filterDateTo || undefined,
      includeVoided: filterIncludeVoided === 'true',
      page: 1,
    }));
  }, [filterMode, filterDateFrom, filterDateTo, filterIncludeVoided]);

  const handleFilterClear = useCallback(() => {
    setFilterMode('');
    setFilterIncludeVoided('false');
    setFilterDateFrom('');
    setFilterDateTo('');
    setQueryParams(prev => ({
      ...prev,
      paymentMode: undefined,
      paymentDateFrom: undefined,
      paymentDateTo: undefined,
      includeVoided: false,
      page: 1,
    }));
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Advance Payments"
        description="Cashier reconciliation of advance payments across all bookings"
        icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : <IconSystem.dollarSign size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search by booking #, guest, or reference...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as ViewMode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: [filterMode, (filterDateFrom || filterDateTo) ? '1' : ''].filter(Boolean).length }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={gridData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No advance payments found"
              frozenLeftColumns={['paymentDate', 'bookingNumber']}
              frozenRightColumns={['actions']}
            />
          )}
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }} title="Close filters">
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Payment Mode"
                  options={paymentModeOptions}
                  value={filterMode}
                  onChange={(val: string | string[] | null) => setFilterMode((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Show"
                  options={includeVoidedOptions}
                  value={filterIncludeVoided}
                  onChange={(val: string | string[] | null) => setFilterIncludeVoided((val as string) || 'false')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Date From</label>
                <input type="date" className="form-input" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Date To</label>
                <input type="date" className="form-input" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleFilterClear}>Clear</button>
              <button className="btn btn-primary" onClick={handleFilterApply}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <PaymentCaptureModal
        isOpen={editModalOpen}
        mode="edit"
        record={selectedRecord}
        bookingId={selectedRecord?.bookingId}
        propertyId={selectedRecord?.propertyId}
        bookingNumber={selectedRecord?.bookingNumber}
        onSave={handleSaveSuccess}
        onClose={() => setEditModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
      />

      <ConfirmDialog
        open={voidConfirmOpen}
        title="Void Payment"
        message={
          selectedRecord
            ? `Voiding will reduce the booking's advance received by ₹${formatCurrency(selectedRecord.amount)}. Continue?`
            : ''
        }
        confirmLabel="Void"
        cancelLabel="Cancel"
        variant="danger"
        loading={isVoiding}
        loadingMessage="Voiding payment..."
        onConfirm={handleConfirmVoid}
        onCancel={() => setVoidConfirmOpen(false)}
      />
    </div>
  );
};

export default AdvancePaymentsScreen;
